import { useState } from 'react';
import useScript from 'react-script-hook';

import { useGetScript } from '../../helpers';

type TagManagerOptions = Record<string, unknown>;

type InitializeTagManagerArgs = {
  bid: string | null | undefined;
  options?: TagManagerOptions;
};

type TagManagerArgs = {
  env: string;
};

type MtMEntry = {
  event?: string;
  'mtm.startTime'?: number;
};

type OnlyfyServerParams = {
  bid?: string | null;
};

type Braze = {
  changeUser: (bid: string | null) => void;
};

declare global {
  var _mtm: MtMEntry[] | undefined;
  var onlyfyServerParams: OnlyfyServerParams | undefined;
  var braze: Braze | undefined;
}

// this is done for testing purposes to reset the singleton instance in the test.
export const alreadyInitiated = { value: false };

type UseTagManagerResult = {
  initializeTagManagerOnce: (args: InitializeTagManagerArgs) => void;
  identifyBrazeUser: (bid: string | null) => void;
  isTagManagerInitiated: boolean;
};

export const useTagManager = ({ env }: TagManagerArgs): UseTagManagerResult => {
  const [loadScript, setLoadScript] = useState(false);
  // We need this outside of the useScript hook so the internal useMemo
  // is always called, to avoid changing the order of called hooks
  const scriptSrc = useGetScript(env);
  useScript({
    src: loadScript ? scriptSrc : null,
    checkForExisting: true,
  });

  const identifyBrazeUser = (bid: string | null): void => {
    if (!alreadyInitiated.value) {
      console.warn('Tag Manager needs to be initiated');
      return;
    }
    const onlyfyServerParams = globalThis.onlyfyServerParams ?? {};
    onlyfyServerParams.bid = bid;
    globalThis.onlyfyServerParams = onlyfyServerParams;
    globalThis.braze?.changeUser(globalThis.onlyfyServerParams.bid ?? null);
  };

  const initializeTagManagerOnce = ({
    bid,
    options,
  }: InitializeTagManagerArgs): void => {
    if (typeof window === 'undefined') {
      return;
    }

    if (alreadyInitiated.value) {
      console.warn('Tag Manager already initiated');
      return;
    }

    alreadyInitiated.value = true;

    // The internal script needs onlyfyServerParams to be defined
    const onlyfyServerParams = globalThis.onlyfyServerParams ?? {};
    onlyfyServerParams.bid = bid;
    globalThis.onlyfyServerParams = onlyfyServerParams;

    const _mtm = globalThis._mtm ?? [];
    _mtm.push({
      'mtm.startTime': new Date().getTime(),
      event: 'mtm.Start',
    });

    if (options) {
      _mtm.push(options);
    }

    globalThis._mtm = _mtm;
    setLoadScript(true);
  };

  return {
    initializeTagManagerOnce,
    identifyBrazeUser,
    isTagManagerInitiated: alreadyInitiated.value,
  };
};
